/**
 * Utility for smart number input stepping with keyboard arrow keys:
 * - ArrowUp / ArrowRight: increment (+)
 * - ArrowDown / ArrowLeft: decrement (-)
 * Increment step is dynamically chosen according to the magnitude/size of the number.
 */

/**
 * Checks whether an element is considered a number/quantity input.
 */
export function isNumberInput(target: EventTarget | null): target is HTMLInputElement {
  if (!target || !(target instanceof HTMLInputElement)) return false
  if (target.disabled || target.readOnly) return false

  const type = (target.type || '').toLowerCase()
  if (type === 'number') return true

  const inputMode = (target.inputMode || '').toLowerCase()
  if (inputMode === 'decimal' || inputMode === 'numeric') return true

  const classList = target.classList
  if (
    classList.contains('amount-input') ||
    classList.contains('quantity-input') ||
    classList.contains('q-input') ||
    classList.contains('popup-input') ||
    classList.contains('input-servings') ||
    classList.contains('servings-form-input') ||
    classList.contains('number-input') ||
    classList.contains('stepper-input') ||
    classList.contains('duration-dialog-input')
  ) {
    return true
  }

  if (target.dataset.numberInput !== undefined) return true

  return false
}

/**
 * Automatically selects the entire text content of a number/quantity input upon focus or click,
 * making it effortless to immediately overwrite the value without tedious manual clearing.
 */
export function handleNumberInputFocusSelect(target: EventTarget | null): void {
  if (!isNumberInput(target)) return

  // Prevent selecting disabled, readonly, or empty inputs
  if (!target.value) return

  try {
    target.select()
  } catch {
    // Some mobile browsers or special types may throw if select() is called; ignore gracefully
  }
}

/**
 * Computes the smart step increment/decrement based on the magnitude of the number.
 */
export function getSmartNumericStep(currentVal: number, direction: 'up' | 'down'): number {
  const abs = Math.abs(currentVal)

  if (direction === 'down') {
    if (abs <= 0.25) return 0.05
    if (abs <= 1) return 0.25
    if (abs <= 10) {
      if (Math.floor(abs) !== abs && abs < 5) return 0.5
      return 1
    }
    if (abs <= 50) return 5
    if (abs <= 100) return 10
    if (abs <= 500) return 25
    if (abs <= 1000) return 50
    if (abs <= 2000) return 100
    return 250
  } else {
    // direction === 'up'
    if (abs === 0) return 1
    if (abs < 0.25) return 0.05
    if (abs < 1) return 0.25
    if (abs < 10) {
      if (Math.floor(abs) !== abs && abs < 5) return 0.5
      return 1
    }
    if (abs < 50) return 5
    if (abs < 100) return 10
    if (abs < 500) return 25
    if (abs < 1000) return 50
    if (abs < 2000) return 100
    return 250
  }
}

/**
 * Applies smart step to a numeric value, clamping between optional min and max.
 */
export function applySmartStep(
  currentVal: number,
  direction: 'up' | 'down',
  min?: number,
  max?: number
): number {
  const step = getSmartNumericStep(currentVal, direction)
  let newVal = direction === 'up' ? currentVal + step : currentVal - step

  // Round to precision matching step decimals to avoid floating point issues (e.g., 0.30000000000000004)
  const stepDecimals = step.toString().split('.')[1]?.length || 0
  const factor = Math.pow(10, stepDecimals)
  newVal = Math.round(newVal * factor) / factor

  if (min !== undefined && newVal < min) newVal = min
  if (max !== undefined && newVal > max) newVal = max
  return newVal
}

/**
 * Handles keydown events on number inputs for ArrowUp/Down/Left/Right.
 * Returns true if handled and prevented, false otherwise.
 */
export function handleNumberInputKeydown(e: KeyboardEvent): boolean {
  if (e.altKey || e.ctrlKey || e.metaKey) return false

  const isUp = e.key === 'ArrowUp' || e.key === 'ArrowRight'
  const isDown = e.key === 'ArrowDown' || e.key === 'ArrowLeft'
  if (!isUp && !isDown) return false

  if (!isNumberInput(e.target)) return false

  const input = e.target
  e.preventDefault()

  const raw = (input.value || '').trim()
  const direction: 'up' | 'down' = isUp ? 'up' : 'down'

  // Determine min and max
  let min: number | undefined
  if (input.min !== '') {
    const parsedMin = parseFloat(input.min)
    if (!isNaN(parsedMin)) min = parsedMin
  } else {
    // Default min to 0 for quantity/amounts
    min = 0
  }

  let max: number | undefined
  if (input.max !== '') {
    const parsedMax = parseFloat(input.max)
    if (!isNaN(parsedMax)) max = parsedMax
  }

  // Parse existing value
  let numVal = 0
  let hasComma = false
  let unitSuffix = ''
  let hasLeadingSpace = false

  if (!raw) {
    numVal = direction === 'up' ? (min !== undefined && min > 0 ? min : 1) : (min !== undefined ? min : 0)
  } else {
    const match = raw.match(/^([+-]?\d+(?:[.,]\d+)?)\s*(.*)$/)
    if (match) {
      hasComma = match[1].includes(',')
      const parsed = parseFloat(match[1].replace(',', '.'))
      numVal = isNaN(parsed) ? 0 : parsed
      unitSuffix = match[2] || ''
      hasLeadingSpace = raw.includes(' ' + unitSuffix) && unitSuffix.length > 0
    } else {
      // String had no number (e.g. just unit text or invalid)
      numVal = direction === 'up' ? (min !== undefined && min > 0 ? min : 1) : 0
      unitSuffix = raw
      hasLeadingSpace = true
    }
  }

  const nextVal = applySmartStep(numVal, direction, min, max)

  // Format string
  let formatted: string
  if (input.type.toLowerCase() === 'number') {
    formatted = nextVal.toString()
  } else {
    const formattedNum = hasComma ? nextVal.toString().replace('.', ',') : nextVal.toString()
    if (unitSuffix) {
      formatted = `${formattedNum}${hasLeadingSpace ? ' ' : ''}${unitSuffix}`
    } else {
      formatted = formattedNum
    }
  }

  input.value = formatted
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))

  return true
}

/**
 * Initializes the global window keydown listener and auto-selection for all number inputs across the app.
 */
export function initGlobalNumberInputHandler(): () => void {
  const keydownListener = (e: KeyboardEvent) => {
    handleNumberInputKeydown(e)
  }

  // Auto-select text on focus
  const focusListener = (e: FocusEvent) => {
    handleNumberInputFocusSelect(e.target)
  }

  // On touch/click, if an unselected number input is focused, selecting on click or pointerup ensures
  // the selection isn't cleared by the browser's default caret positioning.
  let isSelectingFromPointer = false
  const pointerDownListener = (e: Event) => {
    if (isNumberInput(e.target)) {
      if (document.activeElement !== e.target) {
        isSelectingFromPointer = true
      }
    }
  }

  const pointerUpListener = (e: Event) => {
    if (isSelectingFromPointer && isNumberInput(e.target)) {
      isSelectingFromPointer = false
      setTimeout(() => {
        handleNumberInputFocusSelect(e.target)
      }, 0)
    } else {
      isSelectingFromPointer = false
    }
  }

  window.addEventListener('keydown', keydownListener, true)
  window.addEventListener('focusin', focusListener, true)
  window.addEventListener('pointerdown', pointerDownListener, true)
  window.addEventListener('pointerup', pointerUpListener, true)

  return () => {
    window.removeEventListener('keydown', keydownListener, true)
    window.removeEventListener('focusin', focusListener, true)
    window.removeEventListener('pointerdown', pointerDownListener, true)
    window.removeEventListener('pointerup', pointerUpListener, true)
  }
}
